import { nlpIntegration } from '@mi-front/api-clients';
import { useCallback, useMemo, useState } from 'react';
import { usePostHogCaptureLiveTopics } from '../../hooks/usePostHogCaptureLiveTopics';

/**
 * Hook that holds category filter state and derived data for the Live Topics panel.
 * Returns filtered topics (by selected categories), category options from the list,
 * and handlers to toggle/clear selection. Empty selection = show all topics.
 * Captures category_filter_changed when the selection changes.
 */
export function useLiveTopicsCategoryFilter(topics: nlpIntegration.TopicOutDto[]) {
  const [selectedCategories, setSelectedCategoriesState] = useState<string[]>([]);
  const capture = usePostHogCaptureLiveTopics();

  const setSelectedCategories = useCallback(
    (newValue: string[]) => {
      capture('category_filter_changed', { selected_categories: newValue });
      setSelectedCategoriesState(newValue);
    },
    [capture],
  );

  const categoryOptions = useMemo(
    () =>
      [
        ...new Set(topics.map((t) => t.category).filter((c): c is string => c != null && c !== '')),
      ].sort(),
    [topics],
  );

  const filteredTopics = useMemo(
    () =>
      selectedCategories.length === 0
        ? topics
        : topics.filter((t) => t.category != null && selectedCategories.includes(t.category)),
    [topics, selectedCategories],
  );

  const toggleCategory = (category: string) => {
    setSelectedCategoriesState((prev) => {
      const next = prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category];
      capture('category_filter_changed', { selected_categories: next });
      return next;
    });
  };

  const clearSelection = () => {
    capture('category_filter_changed', { selected_categories: [] });
    setSelectedCategoriesState([]);
  };

  return {
    filteredTopics,
    selectedCategories,
    setSelectedCategories,
    categoryOptions,
    toggleCategory,
    clearSelection,
  };
}
